import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { CheckCircle2, XCircle, ShieldAlert, Loader2, Search, ShieldCheck } from "lucide-react";

const TRACK_LABEL: Record<string, string> = {
  writing: "Legal Writing Academy",
  research: "Legal Research Hub",
  court_training: "Court Online Training Centre",
  tutoring: "LLB Tutoring Centre",
};

type VerifyResult =
  | { ok: false; error?: string; status?: "not_found" }
  | {
      ok: true;
      status: "valid" | "revoked";
      certificate: {
        verification_code: string;
        track: string;
        track_ref: string;
        title: string;
        recipient_name: string;
        issued_at: string;
        revoked_at: string | null;
        signature: string | null;
        signature_alg: string | null;
        signed_payload: string | null;
      };
    };

const searchSchema = z.object({
  code: z.string().optional(),
  hash: z.string().optional(),
});

export const Route = createFileRoute("/verify")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Verify Certificate — Nkadimeng Danny Legal Education" },
      {
        name: "description",
        content:
          "Verify a certificate of completion issued by the Nkadimeng Danny Legal & Compliance Support Education Division. Enter the code or scan the QR on your certificate.",
      },
      { property: "og:title", content: "Verify a legal-education certificate" },
      {
        property: "og:description",
        content:
          "Public verification for certificates issued by the Education & Professional Development Division.",
      },
    ],
  }),
  component: VerifyPage,
});

function VerifyPage() {
  const { code: initialCode, hash: initialHash } = Route.useSearch();
  const [mode, setMode] = useState<"code" | "hash">(
    initialHash ? "hash" : "code",
  );
  const [code, setCode] = useState((initialCode ?? "").toUpperCase());
  const [hash, setHash] = useState((initialHash ?? "").toLowerCase());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [autoRan, setAutoRan] = useState(false);

  async function lookupCode(c: string) {
    const clean = c.trim().toUpperCase();
    if (!/^[A-Z0-9]{6,32}$/.test(clean)) {
      setResult({
        ok: false,
        error: "Enter a valid verification code (letters and numbers).",
      });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(
        `/api/public/verify-certificate/${encodeURIComponent(clean)}`,
      );
      const data = (await res.json()) as VerifyResult;
      setResult(data);
    } catch {
      setResult({
        ok: false,
        error: "Could not reach the verification service. Try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function lookupHash(h: string) {
    const clean = h.trim().toLowerCase();
    if (!/^[a-f0-9]{64}$/.test(clean)) {
      setResult({
        ok: false,
        error:
          "Enter a full 64-character SHA-256 hash (hex). It's printed on the bottom of the PDF.",
      });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(
        `/api/public/verify-certificate-hash/${encodeURIComponent(clean)}`,
      );
      const data = (await res.json()) as VerifyResult;
      setResult(data);
    } catch {
      setResult({
        ok: false,
        error: "Could not reach the verification service. Try again.",
      });
    } finally {
      setLoading(false);
    }
  }

  // Auto-lookup once if a code or hash was passed via search params.
  if (!autoRan && !loading && !result) {
    if (initialCode) {
      setAutoRan(true);
      lookupCode(initialCode);
    } else if (initialHash) {
      setAutoRan(true);
      lookupHash(initialHash);
    }
  }

  return (
    <div className="container-narrow py-12">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Certificate verification</p>
      <h1 className="mt-2 font-display text-4xl text-navy">Verify a certificate</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Every certificate issued by our Education & Professional Development Division carries a unique
        verification code and QR. Enter the code below (or scan the QR on the certificate) to confirm
        it is authentic and current.
      </p>

      <div className="mt-6 inline-flex rounded-md border border-input bg-muted/40 p-1 text-xs">
        <button
          type="button"
          onClick={() => setMode("code")}
          className={`rounded px-3 py-1.5 ${mode === "code" ? "bg-navy text-navy-foreground" : "text-muted-foreground hover:text-navy"}`}
        >
          Verification code / QR
        </button>
        <button
          type="button"
          onClick={() => setMode("hash")}
          className={`rounded px-3 py-1.5 ${mode === "hash" ? "bg-navy text-navy-foreground" : "text-muted-foreground hover:text-navy"}`}
        >
          Document hash (SHA-256)
        </button>
      </div>

      {mode === "code" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            lookupCode(code);
          }}
          className="mt-4 flex max-w-xl flex-col gap-3 sm:flex-row"
        >
          <input
            aria-label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. A1B2C3D4E5F6"
            className="flex-1 rounded-md border border-input bg-background px-4 py-2.5 text-sm font-mono tracking-wider"
            maxLength={32}
          />
          <button
            type="submit"
            disabled={loading || !code}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Verify
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            lookupHash(hash);
          }}
          className="mt-4 flex max-w-xl flex-col gap-3 sm:flex-row"
        >
          <input
            aria-label="Certificate SHA-256 hash"
            value={hash}
            onChange={(e) =>
              setHash(e.target.value.toLowerCase().replace(/\s+/g, ""))
            }
            placeholder="Paste the 64-character SHA-256 shown on the PDF"
            className="flex-1 rounded-md border border-input bg-background px-4 py-2.5 text-xs font-mono"
            maxLength={64}
          />
          <button
            type="submit"
            disabled={loading || hash.length < 64}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            Verify hash
          </button>
        </form>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">
        The SHA-256 hash is printed on the bottom-left of the certificate PDF.
        A matching hash proves the PDF is bit-for-bit the copy we issued.
      </p>

      <div className="mt-8 max-w-2xl">
        {result === null && !loading && (
          <div className="rounded-md border border-dashed border-input bg-muted/30 p-6 text-sm text-muted-foreground">
            Enter a certificate code to verify its authenticity.
          </div>
        )}

        {result && result.ok && result.status === "valid" && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-7 w-7 text-emerald-700" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                  Valid certificate
                </p>
                <h2 className="font-display text-xl text-navy">{result.certificate.title}</h2>
              </div>
            </div>
            <dl className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Recipient</dt>
                <dd className="mt-0.5 font-medium text-navy">{result.certificate.recipient_name}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Track</dt>
                <dd className="mt-0.5 font-medium text-navy">
                  {TRACK_LABEL[result.certificate.track] ?? result.certificate.track}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Reference</dt>
                <dd className="mt-0.5 font-mono text-foreground">{result.certificate.track_ref}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Issued</dt>
                <dd className="mt-0.5 font-medium text-navy">
                  {new Date(result.certificate.issued_at).toLocaleDateString("en-ZA", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Verification code</dt>
                <dd className="mt-0.5 font-mono text-sm tracking-widest text-navy">
                  {result.certificate.verification_code}
                </dd>
              </div>
            </dl>

            {result.certificate.signature && (
              <div className="mt-5 rounded-md border border-emerald-300 bg-white/60 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-800">
                  <ShieldCheck className="h-4 w-4" /> Cryptographic signature on file
                </p>
                <p className="mt-1.5 text-xs text-emerald-900">
                  Compare the <span className="font-mono">HMAC-SHA256</span> line printed on the bottom-left of
                  the PDF to the value below. If they do not match exactly, the PDF has been altered after issue.
                </p>
                <p className="mt-2 break-all rounded bg-emerald-100/60 p-2 font-mono text-[10px] text-emerald-900">
                  {result.certificate.signature}
                </p>
                <p className="mt-1 text-[10px] text-emerald-800">
                  Algorithm: {result.certificate.signature_alg ?? "HMAC-SHA256"}
                </p>
              </div>
            )}

            <p className="mt-5 flex items-start gap-2 text-xs text-emerald-900">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              This certificate is on record and has not been revoked. It confirms participation in legal
              education and professional development training — it is not a qualification or licence to practise law.
            </p>
          </div>
        )}

        {result && result.ok && result.status === "revoked" && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-6">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-7 w-7 text-amber-700" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">
                  Certificate revoked
                </p>
                <h2 className="font-display text-xl text-navy">{result.certificate.title}</h2>
              </div>
            </div>
            <p className="mt-3 text-sm text-amber-900">
              This certificate was issued to <strong>{result.certificate.recipient_name}</strong> on{" "}
              {new Date(result.certificate.issued_at).toLocaleDateString("en-ZA")} but has been revoked by
              the Education Division on{" "}
              {result.certificate.revoked_at
                ? new Date(result.certificate.revoked_at).toLocaleDateString("en-ZA")
                : "unknown date"}
              . Do not rely on it.
            </p>
          </div>
        )}

        {result && !result.ok && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-6">
            <div className="flex items-center gap-3">
              <XCircle className="h-7 w-7 text-rose-700" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-rose-700">
                  {result.status === "not_found" ? "Certificate not found" : "Cannot verify"}
                </p>
                <p className="text-sm text-rose-900">
                  {result.error ??
                    "No certificate with that code is on record. Please check the code and try again, or contact us if you believe this is an error."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-12 rounded-md border border-input bg-card p-5 text-sm text-muted-foreground">
        Looking for the Education Division?{" "}
        <Link to="/education" className="font-semibold text-navy underline-offset-4 hover:underline">
          Browse our learning tracks
        </Link>
        .
      </div>
    </div>
  );
}
